import { useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { CURRENT_REPORT_GROUP_ID } from '../comparisonGroups';
import type { NewReport } from '../hooks/useNewReport';
import type { AnswerGroup } from '../types';
import { AnswerGroupComposer } from './AnswerGroupComposer';
import { answerGroupFilterLabels } from './answerGroupFilterLabels';
import {
  Badge,
  Bar,
  Check,
  CheckList,
  ColumnCard,
  Field,
  Hint,
  IconAction,
  OptionsGrid,
  Stack,
} from './tablePlaygroundStyles';

export function ComparisonGroupPicker({
  report,
  selectedIds,
  setSelectedIds,
}: {
  report: NewReport;
  selectedIds: Set<string>;
  setSelectedIds: Dispatch<SetStateAction<Set<string>>>;
}) {
  const { t } = useTranslation('newReport');
  const [search, setSearch] = useState('');
  const [feedback, setFeedback] = useState<{ label: string; existing: boolean } | null>(null);
  const teams = useMemo(() => {
    const options = new Map<string, string>();
    const visit = (group: AnswerGroup, parent: string) => {
      const label = parent ? `${parent} / ${group.label}` : group.label;
      options.set(group.id, label);
      group.children.forEach((child) => visit(child, label));
    };
    (report.answerGroupTree ?? []).forEach((group) => visit(group, ''));
    return [...options].map(([id, label]) => ({ id, label }));
  }, [report.answerGroupTree]);
  const toggle = (id: string, checked: boolean) =>
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  return (
    <Stack>
      <Hint>{t('tableTest.compareGroupsHint')}</Hint>
      <OptionsGrid>
        <Stack>
          <Stack>
            <h3>{t('tableTest.yourAnswerGroups')}</h3>
            <Hint>{t('tableTest.savedGroupsHint')}</Hint>
            {!report.adHocGroups.size && <Hint>{t('tableTest.noAnswerGroups')}</Hint>}
            {[...report.adHocGroups].map(([key, group]) => {
              const config = report.adHocGroupConfigs.get(key);
              const rules = config ? answerGroupFilterLabels(config, report.rawData, t) : [];
              return (
                <ColumnCard key={key} selected={selectedIds.has(group.id)}>
                  <Stack>
                    <Bar>
                      <Check>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(group.id)}
                          onChange={(event) => toggle(group.id, event.target.checked)}
                        />
                        <strong>{group.label}</strong>
                      </Check>
                      <Badge>{t('tableTest.groupParticipants', { count: group.respondentCount })}</Badge>
                      <IconAction
                        type="button"
                        aria-label={t('tableTest.removeAnswerGroup', { group: group.label })}
                        title={t('tableTest.removeAnswerGroup', { group: group.label })}
                        onClick={() => {
                          report.removeAnswerGroup(key);
                          toggle(group.id, false);
                        }}
                      >
                        ×
                      </IconAction>
                    </Bar>
                    {rules.map((rule, index) => (
                      <Hint key={index}>{rule}</Hint>
                    ))}
                  </Stack>
                </ColumnCard>
              );
            })}
            {feedback && (
              <Hint role="status">
                {t(feedback.existing ? 'tableTest.groupAlreadyExists' : 'tableTest.groupCreated', {
                  group: feedback.label,
                })}
              </Hint>
            )}
          </Stack>
          <Stack>
            <h3>{t('tableTest.liveReportGroups')}</h3>
            <Hint>{t('tableTest.liveGroupsHint')}</Hint>
            <Check>
              <input
                type="checkbox"
                checked={selectedIds.has(CURRENT_REPORT_GROUP_ID)}
                onChange={(event) => toggle(CURRENT_REPORT_GROUP_ID, event.target.checked)}
              />
              <strong>{t('tableTest.currentReport')}</strong>
              <Badge>{t('tableTest.liveFilters')}</Badge>
            </Check>
            <Field>
              {t('tableTest.findTeam')}
              <input value={search} onChange={(event) => setSearch(event.target.value)} />
            </Field>
            <CheckList>
              {teams
                .filter((team) => team.label.toLocaleLowerCase().includes(search.toLocaleLowerCase()))
                .map((team) => (
                  <Check key={team.id}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(team.id)}
                      onChange={(event) => toggle(team.id, event.target.checked)}
                    />
                    {team.label}
                  </Check>
                ))}
            </CheckList>
          </Stack>
        </Stack>
        <AnswerGroupComposer
          report={report}
          onCreated={(group, existing) => {
            toggle(group.id, true);
            setFeedback({ label: group.label, existing });
          }}
        />
      </OptionsGrid>
      {selectedIds.size === 0 && <Hint>{t('tableTest.chooseComparisonGroup')}</Hint>}
    </Stack>
  );
}
